import { useMemo } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { DEFAULT_CURRENCY } from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'
import i18n from 'modules/core/lib/i18n'

interface UseDataCollectionColumnsOptions {
  title: string
  headers: string[]
  showComparison?: boolean
  comparisonHeaders?: string[]
  groupBy: InsightTableGroupBy
  headerColumnLabels: { [key: string]: string | undefined }
}

/**
 * Generates dynamic column definitions for OneDataCollection table
 * Handles both regular and comparison modes with appropriate column structures
 */
export const useDataCollectionColumns = ({
  title,
  headers,
  groupBy,
  comparisonHeaders,
  headerColumnLabels,
  showComparison = false,
}: UseDataCollectionColumnsOptions) => {
  const { company } = useCurrentSession()
  const { supportedCurrencies } = useFinancialWorkspace()

  const currency = supportedCurrencies.includes(String(company.currency))
    ? company.currency
    : DEFAULT_CURRENCY

  const locale = company.locale ?? 'en'

  return useMemo(() => {
    const formatAmount = (amountInCents: string | undefined): string => {
      const amount = Number(amountInCents) || 0
      if (!isCurrency(String(currency))) return '-'
      return MoneyWithCurrency({
        amount,
        currency: String(currency),
        locale,
        optionalDecimals: false,
      })
    }

    const labelColumn = {
      label: title,
      align: 'left' as const,
      render: (item: FinancialWorkspaceRecord) => {
        if (groupBy === InsightTableGroupBy.Employee && item.level === 0) {
          const nameParts = (item.label ?? '').split(' ')
          const firstName = nameParts[0] ?? ''
          const lastName = nameParts.slice(1).join(' ') ?? ''
          return {
            type: 'person' as const,
            value: {
              lastName,
              firstName,
              avatar: item.imageUrl,
            },
          }
        }

        const subLabel = i18n.t('finance.financial_workspace.employee_costs.table.agreement_data.sub_label')
        const label = `${item.label ?? ''}${item.id === "agreement" ? ` ${subLabel}` : ''}`

        return {
          value: label,
          type: 'text' as const,
        }
      },
    }

    if (headers.length === 0) {
      return [labelColumn]
    }

    // In comparison mode, useHeaders() provides 3 headers per period:
    // [comparison_date, current_date, "Growth", ...] so we create one column per header
    const periodColumns = headers.map((label, index) => {
      const headerLabel = headerColumnLabels[label]
      const comparisonHeaderGroupId = comparisonHeaders?.[Math.floor(index / 3)]

      if (showComparison) {
        const positionInTriplet = index % 3

        if (positionInTriplet === 0) {
          return {
            id: `comparison-${index}`,
            label: headerLabel ?? label,
            headerGroupId: comparisonHeaderGroupId,
            align: 'right' as const,
            render: (item: FinancialWorkspaceRecord) => {
              const comparisonValue = item.comparisonValues?.find((cv) => cv.label === label)
              return {
                type: 'text' as const,
                value: formatAmount(comparisonValue?.amountInCents),
              }
            },
          }
        } else if (positionInTriplet === 1) {
          return {
            id: `current-${index}`,
            label: headerLabel ?? label,
            headerGroupId: comparisonHeaderGroupId,
            align: 'right' as const,
            render: (item: FinancialWorkspaceRecord) => {
              const currentValue = item.periodValues?.find((pv) => pv.label === label)
              return {
                type: 'text' as const,
                value: formatAmount(currentValue?.amountInCents),
              }
            },
          }
        } else {
          const currentLabel = headers[index - 1]
          const comparisonLabel = headers[index - 2]

          return {
            id: `growth-${index}`,
            label: headerLabel ?? label,
            headerGroupId: comparisonHeaderGroupId,
            align: 'right' as const,
            render: (item: FinancialWorkspaceRecord) => {
              const currentValue =
                Number(item.periodValues?.find((pv) => pv.label === currentLabel)?.amountInCents) ||
                0
              const comparisonValue =
                Number(
                  item.comparisonValues?.find((cv) => cv.label === comparisonLabel)?.amountInCents
                ) || 0

              if (!comparisonValue || comparisonValue === 0) {
                return {
                  type: 'text' as const,
                  value: '-',
                }
              }

              const percentage =
                ((currentValue - comparisonValue) / Math.abs(comparisonValue)) * 100
              const absPercentage = Math.abs(percentage).toFixed(2)

              if (absPercentage === '0.00') {
                return {
                  type: 'text' as const,
                  value: '-',
                }
              }

              const isPositive = percentage > 0

              return {
                type: 'delta' as const,
                value: {
                    label: `${absPercentage}%`,
                    deltaStatus: isPositive ? 'positive' : 'negative'
                }
              }
            },
          }
        }
      }

      // Non-comparison mode: single column per period
      return {
        id: `period-${index}`,
        label: headerLabel ?? label,
        align: 'right' as const,
        render: (item: FinancialWorkspaceRecord) => {
          const value = item.periodValues?.find((pv) => pv.label === label)
          if (!value) {
            return {
              type: 'text' as const,
              value: '-',
            }
          }

          return {
            type: 'text' as const,
            value: formatAmount(value.amountInCents),
          }
        },
      }
    }).filter(Boolean)

    return [labelColumn, ...periodColumns]
  }, [title, headers, groupBy, showComparison, comparisonHeaders, currency, locale, headerColumnLabels])
}
